import { z } from "zod";

/**
 * Open Graph metadata schema
 */
const OpenGraphSchema = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    image: z.string().url().optional(),
    imageAlt: z.string().optional(),
    type: z.enum(["website", "article", "business.business"]).default("website"),
    locale: z.string().default("en_US"),
    siteName: z.string().optional(),
  }),
  /**
   * Twitter Card metadata schema
   */
  TwitterCardSchema = z.object({
    card: z.enum(["summary", "summary_large_image", "app", "player"]).default("summary_large_image"),
    site: z.string().optional(), // @username
    creator: z.string().optional(), // @username
    title: z.string().optional(),
    description: z.string().optional(),
    image: z.string().url().optional(),
    imageAlt: z.string().optional(),
  }),
  /**
   * JSON-LD Organization schema
   */
  OrganizationSchema = z.object({
    "@type": z.literal("Organization").default("Organization"),
    name: z.string(),
    url: z.string().url(),
    logo: z.string().url().optional(),
    description: z.string().optional(),
    telephone: z.string().optional(),
    email: z.string().email().optional(),
    address: z
      .object({
        "@type": z.literal("PostalAddress").default("PostalAddress"),
        streetAddress: z.string().optional(),
        addressLocality: z.string().optional(), // City
        addressRegion: z.string().optional(), // State
        postalCode: z.string().optional(),
        addressCountry: z.string().default("US"),
      })
      .optional(),
    sameAs: z.array(z.string().url()).optional(), // Social media URLs
  }),
  /**
   * JSON-LD LocalBusiness schema
   */
  LocalBusinessSchema = z.object({
    // A schema.org LocalBusiness subtype when the trade has one
    // (Plumber, Electrician, HVACBusiness, …); generic LocalBusiness otherwise.
    "@type": z.string().default("LocalBusiness"),
    name: z.string(),
    description: z.string().optional(),
    sameAs: z.array(z.string()).optional(),
    areaServed: z.array(z.record(z.string(), z.any())).optional(),
    hasOfferCatalog: z.record(z.string(), z.any()).optional(),
    hasCredential: z.record(z.string(), z.any()).optional(),
    image: z.string().url().optional(),
    url: z.string().url(),
    telephone: z.string(),
    email: z.string().email().optional(),
    address: z.object({
      "@type": z.literal("PostalAddress").default("PostalAddress"),
      streetAddress: z.string(),
      addressLocality: z.string(), // City
      addressRegion: z.string(), // State
      postalCode: z.string(),
      addressCountry: z.string().default("US"),
    }),
    geo: z
      .object({
        "@type": z.literal("GeoCoordinates").default("GeoCoordinates"),
        latitude: z.number(),
        longitude: z.number(),
      })
      .optional(),
    openingHoursSpecification: z
      .array(
        z.object({
          "@type": z.literal("OpeningHoursSpecification").default("OpeningHoursSpecification"),
          dayOfWeek: z.union([z.string(), z.array(z.string())]),
          opens: z.string(), // E.g., "09:00"
          closes: z.string(), // E.g., "17:00"
        }),
      )
      .optional(),
    priceRange: z.string().optional(), // E.g., "$$"
    servesCuisine: z.string().optional(),
    acceptsReservations: z.boolean().optional(),
    aggregateRating: z
      .object({
        "@type": z.literal("AggregateRating").default("AggregateRating"),
        ratingValue: z.number().min(0).max(5),
        reviewCount: z.number().int().positive(),
        bestRating: z.number().default(5),
        worstRating: z.number().default(1),
      })
      .optional(),
  }),
  /**
   * Robots meta tag options
   */
  RobotsSchema = z.object({
    index: z.boolean().default(true),
    follow: z.boolean().default(true),
    noarchive: z.boolean().optional(),
    nosnippet: z.boolean().optional(),
    noimageindex: z.boolean().optional(),
    maxSnippet: z.number().optional(), // Max characters in snippet
    maxImagePreview: z.enum(["none", "standard", "large"]).optional(),
    maxVideoPreview: z.number().optional(), // Max seconds of video preview
  }),
  /**
   * Analytics configuration
   */
  AnalyticsSchema = z.object({
    googleAnalytics: z.string().optional(), // GA4 Measurement ID (G-XXXXXXXXXX)
    googleTagManager: z.string().optional(), // GTM ID (GTM-XXXXXX)
    facebookPixel: z.string().optional(),
    linkedInInsight: z.string().optional(),
  }),
  /**
   * Page-level SEO metadata
   */
  PageMetadataSchema = z.object({
    title: z.string().min(1).max(60), // Optimal: 50-60 characters
    description: z.string().min(1).max(160), // Optimal: 150-160 characters
    keywords: z.array(z.string()).optional(),
    canonical: z.string().url().optional(),
    openGraph: OpenGraphSchema.optional(),
    twitter: TwitterCardSchema.optional(),
    robots: RobotsSchema.optional(),
    noindex: z.boolean().default(false),
    nofollow: z.boolean().default(false),
    // Structured data for this specific page
    jsonLd: z.array(z.record(z.string(), z.any())).optional(),
  }),
  /**
   * Site-level SEO metadata configuration
   */
  SiteMetadataSchema = z.object({
    // Basic info
    businessName: z.string(),
    domain: z.string().url(),
    faviconUrl: z.string().optional(),

    // Default SEO
    defaultTitle: z.string(),
    titleTemplate: z.string().default("%s | {businessName}"), // {businessName} will be replaced
    defaultDescription: z.string(),
    defaultKeywords: z.array(z.string()).optional(),

    // Social & Open Graph
    openGraph: OpenGraphSchema,
    twitter: TwitterCardSchema,

    // Structured Data
    organization: OrganizationSchema.optional(),
    localBusiness: LocalBusinessSchema.optional(),

    // Technical SEO
    robots: RobotsSchema.default({
      index: true,
      follow: true,
    }),

    // Analytics
    analytics: AnalyticsSchema.optional(),

    // Sitemap
    sitemapChangeFreq: z
      .enum(["always", "hourly", "daily", "weekly", "monthly", "yearly", "never"])
      .default("weekly"),
    sitemapPriority: z.number().min(0).max(1).default(0.7),
  }),
  /**
   * Helper to generate robots meta content string
   */
  robotsToString = (robots: RobotsMetadata): string => {
    const parts: string[] = [];

    if (robots.index === false) {
      parts.push("noindex");
    }
    if (robots.follow === false) {
      parts.push("nofollow");
    }
    if (robots.noarchive === true) {
      parts.push("noarchive");
    }
    if (robots.nosnippet === true) {
      parts.push("nosnippet");
    }
    if (robots.noimageindex === true) {
      parts.push("noimageindex");
    }
    if (robots.maxSnippet !== undefined) {
      parts.push(`max-snippet:${robots.maxSnippet}`);
    }
    if (robots.maxImagePreview) {
      parts.push(`max-image-preview:${robots.maxImagePreview}`);
    }
    if (robots.maxVideoPreview !== undefined) {
      parts.push(`max-video-preview:${robots.maxVideoPreview}`);
    }

    // If nothing set, default to "index, follow"
    if (parts.length === 0) {
      return "index, follow";
    }

    return parts.join(", ");
  },
  /**
   * Helper to merge page and site metadata
   */
  mergeMetadata = (
    siteMetadata: SiteMetadata,
    pageMetadata: Partial<PageMetadata>,
  ): Required<PageMetadata> => {
    const title = pageMetadata.title || siteMetadata.defaultTitle,
      titleTemplate = siteMetadata.titleTemplate || "%s",
      businessName = siteMetadata.businessName || "",
      normalizedTitle = title.trim().toLowerCase(),
      normalizedBusiness = businessName.trim().toLowerCase(),
      shouldApplyTemplate =
        titleTemplate !== "%s" && Boolean(normalizedBusiness) && !normalizedTitle.includes(normalizedBusiness),
      titleWithTemplate = shouldApplyTemplate
        ? titleTemplate.replace("{businessName}", businessName).replace("%s", title)
        : title,

      // Page schemas only. The site-level Organization and LocalBusiness are
      // Injected into <head> by prerender.js, straight from the same site data —
      // Including them here too emitted each of them twice per page, once in the
      // Head and once in the body, which is two nodes describing one business.
      pageSchemas = (pageMetadata.jsonLd || []) as Record<string, unknown>[];

    return {
      canonical: pageMetadata.canonical || "",
      description: pageMetadata.description || siteMetadata.defaultDescription || "",
      jsonLd: pageSchemas,
      keywords: pageMetadata.keywords || siteMetadata.defaultKeywords || [],
      nofollow: pageMetadata.nofollow || false,
      noindex: pageMetadata.noindex || false,
      openGraph: {
        ...siteMetadata.openGraph,
        ...pageMetadata.openGraph,
        description:
          pageMetadata.openGraph?.description ||
          pageMetadata.description ||
          siteMetadata.defaultDescription ||
          "",
        title: pageMetadata.openGraph?.title || title,
      },
      robots: pageMetadata.robots || siteMetadata.robots || { follow: true, index: true },
      title: titleWithTemplate,
      twitter: {
        ...siteMetadata.twitter,
        ...pageMetadata.twitter,
        description:
          pageMetadata.twitter?.description ||
          pageMetadata.description ||
          siteMetadata.defaultDescription ||
          "",
        title: pageMetadata.twitter?.title || title,
      },
    };
  };

type OpenGraphMetadata = z.infer<typeof OpenGraphSchema>;

type TwitterCardMetadata = z.infer<typeof TwitterCardSchema>;

type OrganizationData = z.infer<typeof OrganizationSchema>;

type LocalBusinessData = z.infer<typeof LocalBusinessSchema>;

type RobotsMetadata = z.infer<typeof RobotsSchema>;

type AnalyticsConfig = z.infer<typeof AnalyticsSchema>;

type PageMetadata = z.infer<typeof PageMetadataSchema>;

type SiteMetadata = z.infer<typeof SiteMetadataSchema>;

export type {
  OpenGraphMetadata,
  TwitterCardMetadata,
  OrganizationData,
  LocalBusinessData,
  RobotsMetadata,
  AnalyticsConfig,
  PageMetadata,
  SiteMetadata,
};
export {
  OpenGraphSchema,
  TwitterCardSchema,
  OrganizationSchema,
  LocalBusinessSchema,
  RobotsSchema,
  AnalyticsSchema,
  PageMetadataSchema,
  SiteMetadataSchema,
  robotsToString,
  mergeMetadata,
};
